import React, { useState } from 'react';
import styled from 'styled-components';
import { registrationService } from '../../services/apiService';

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1.5rem;

  img {
    height: 50px;
    width: auto;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  @media (max-width: 768px) {
    gap: 1rem;
    img {
      height: 40px;
    }
  }
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  padding: 2rem;
  text-align: center;
  border-bottom: 3px solid #059669;
  margin-bottom: 2rem;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  h1 {
    margin: 0.5rem 0 0.25rem 0;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  .subtitle {
    font-size: 0.9rem;
    opacity: 0.85;
    margin: 0.5rem 0;
    font-weight: 500;
  }

  .association {
    font-size: 0.8rem;
    opacity: 0.8;
    margin-top: 0.75rem;
    font-style: italic;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    h1 {
      font-size: 1.6rem;
    }
    .subtitle {
      font-size: 0.85rem;
    }
    .association {
      font-size: 0.75rem;
    }
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormWrapper = styled.div`
  padding: 2rem;
`;

const FormTitle = styled.h1`
  color: #333;
  margin-bottom: 2rem;
  text-align: center;
  border-bottom: 3px solid #667eea;
  padding-bottom: 1rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f9f9f9;
  border-radius: 6px;
  border-left: 4px solid #667eea;
`;

const SectionTitle = styled.h2`
  color: #667eea;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '${props => props.icon}';
    font-size: 1.5rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthRow = styled(FormRow)`
  grid-template-columns: 1fr;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 0.95rem;

  span {
    color: #f44336;
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:hover {
    border-color: #bbb;
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 2px dashed #667eea;
  border-radius: 4px;
  cursor: pointer;
  background: #f0f0f0;

  &:focus {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: #667eea;
    color: white;

    &:hover {
      background: #5568d3;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  }

  &.secondary {
    background: #ddd;
    color: #333;

    &:hover {
      background: #bbb;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  background: ${props => props.type === 'error' ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.type === 'error' ? '#c62828' : '#2e7d32'};
  border-left: 4px solid ${props => props.type === 'error' ? '#f44336' : '#4caf50'};
`;

const FormFooter = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
  color: #999;
  font-size: 0.85rem;
  font-weight: 500;
`;

const SellerRegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    pan: '',
    gstin: '',
    bankAccountNumber: '',
    ifscCode: '',
    cancelledChequeUrl: '',
    state: '',
    region: '',
    // Farm details
    farmName: '',
    latitude: 0,
    longitude: 0,
    areaInAcres: 0,
    addressLine1: '',
    addressLine2: '',
    district: '',
    pincode: '',
    primaryCropType: '',
  });
  
  const [rtcFile, setRtcFile] = useState(null);
  
  const [farms, setFarms] = useState([{
    farmName: '',
    latitude: 0,
    longitude: 0,
    areaInAcres: 0,
    addressLine1: '',
    addressLine2: '',
    district: '',
    pincode: '',
    crops: [{ cropType: '', distributionPercentage: 0 }]
  }]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: ['latitude', 'longitude', 'areaInAcres'].includes(name) ? parseFloat(value) : value 
    }));
  };

  const handleFarmChange = (farmIndex, field, value) => {
    const newFarms = [...farms];
    newFarms[farmIndex][field] = ['latitude', 'longitude', 'areaInAcres'].includes(field) ? parseFloat(value) : value;
    setFarms(newFarms);
  };

  const handleFarmCropChange = (farmIndex, cropIndex, field, value) => {
    const newFarms = [...farms];
    newFarms[farmIndex].crops[cropIndex][field] = field === 'distributionPercentage' ? parseFloat(value) : value;
    setFarms(newFarms);
  };

  const addFarmField = () => {
    setFarms([
      ...farms,
      {
        farmName: '',
        latitude: 0,
        longitude: 0,
        areaInAcres: 0,
        addressLine1: '',
        addressLine2: '',
        district: '',
        pincode: '',
        crops: [{ cropType: '', distributionPercentage: 0 }]
      }
    ]);
  };

  const removeFarmField = (farmIndex) => {
    if (farms.length > 1) {
      setFarms(farms.filter((_, i) => i !== farmIndex));
    }
  };

  const addFarmCropField = (farmIndex) => {
    const newFarms = [...farms];
    newFarms[farmIndex].crops.push({ cropType: '', distributionPercentage: 0 });
    setFarms(newFarms);
  };

  const removeFarmCropField = (farmIndex, cropIndex) => {
    const newFarms = [...farms];
    if (newFarms[farmIndex].crops.length > 1) {
      newFarms[farmIndex].crops = newFarms[farmIndex].crops.filter((_, i) => i !== cropIndex);
      setFarms(newFarms);
    }
  };

  const handleRtcFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        setError('RTC document must be less than 1MB');
        return;
      }
      setRtcFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        setError('Password and confirm password do not match');
        setLoading(false);
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      // Validate farms
      if (!farms || farms.length === 0) {
        setError('At least one farm is required');
        setLoading(false);
        return;
      }

      for (let i = 0; i < farms.length; i++) {
        if (!farms[i].farmName || !farms[i].addressLine1 || !farms[i].district) {
          setError(`Farm ${i + 1}: Farm name, address, and district are required`);
          setLoading(false);
          return;
        }
        if (!farms[i].crops || farms[i].crops.length === 0) {
          setError(`Farm ${i + 1}: At least one crop is required`);
          setLoading(false);
          return;
        }
      }

      if (!formData.primaryCropType) {
        setError('Primary crop type is required');
        setLoading(false);
        return;
      }

      // Register seller with all details
      const registrationId = await registrationService.registerSeller(
        formData.phoneNumber,
        formData.email,
        formData.firstName,
        formData.lastName,
        formData.password,
        formData.pan,
        formData.gstin,
        formData.bankAccountNumber,
        formData.ifscCode,
        formData.cancelledChequeUrl,
        formData.state,
        formData.region,
        farms[0].farmName,
        farms[0].latitude,
        farms[0].longitude,
        farms[0].areaInAcres,
        farms[0].addressLine1,
        farms[0].addressLine2,
        farms[0].district,
        farms[0].pincode,
        formData.primaryCropType,
        farms  // Pass all farms
      );

      // Upload RTC document if provided
      if (rtcFile && registrationId) {
        try {
          await registrationService.uploadRtcDocument(registrationId, rtcFile);
          console.log('✅ RTC document uploaded successfully');
        } catch (rtcError) {
          console.error('⚠️ RTC upload failed:', rtcError);
        }
      }

      setSuccess('✅ Registration submitted! Admin will review your request within 24 hours. You will receive an email once approved.');
      setFormData({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        pan: '',
        gstin: '',
        bankAccountNumber: '',
        ifscCode: '',
        cancelledChequeUrl: '',
        state: '',
        region: '',
        farmName: '',
        latitude: 0,
        longitude: 0,
        areaInAcres: 0,
        addressLine1: '',
        addressLine2: '',
        district: '',
        pincode: '',
      });
      setFarms([{
        farmName: '',
        latitude: 0,
        longitude: 0,
        areaInAcres: 0,
        addressLine1: '',
        addressLine2: '',
        district: '',
        pincode: '',
        crops: [{ cropType: '', distributionPercentage: 0 }]
      }]);
      setRtcFile(null);
    } catch (err) {
      setError(err.message || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <LogoContainer>
          <img src="/my-mango-logo.png" alt="Mango Logo" />
        </LogoContainer>
        <h1>🌾 Buyer Seller Platform</h1>
        <div className="subtitle">Register as a Seller</div>
        <div className="association">Mango Growers' Association, Dharwad</div>
      </PageHeader>

      <FormWrapper>
        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <FormSection>
          <SectionTitle icon="�">RTC Document (Optional)</SectionTitle>
          <FullWidthRow>
            <FormGroup>
              <Label>RTC Document (Rural Tariff Card)</Label>
              <FileInput
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleRtcFileSelect}
              />
              {rtcFile && <p style={{ color: '#4caf50', marginTop: '0.5rem' }}>✓ {rtcFile.name} ({(rtcFile.size / 1024).toFixed(2)} KB)</p>}
              <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>Max 1MB. Formats: PDF, JPG, PNG</p>
            </FormGroup>
          </FullWidthRow>
        </FormSection>

        <FormSection>
          <SectionTitle icon="�👤">Personal Information</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>First Name <span>*</span></Label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Your first name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Last Name <span>*</span></Label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Your last name"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Phone Number <span>*</span></Label>
              <Input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="+91 9876543210"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email Address <span>*</span></Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seller@example.com"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Password <span>*</span></Label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Minimum 6 characters"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Confirm Password <span>*</span></Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Re-enter password"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Banking Details */}
        <FormSection>
          <SectionTitle icon="🏦">Banking Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>PAN <span>*</span></Label>
              <Input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
                placeholder="AAAPA5055K"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>GSTIN <span>*</span></Label>
              <Input
                type="text"
                name="gstin"
                value={formData.gstin}
                onChange={handleInputChange}
                placeholder="29AAFCU5055K1Z0"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Bank Account Number <span>*</span></Label>
              <Input
                type="text"
                name="bankAccountNumber"
                value={formData.bankAccountNumber}
                onChange={handleInputChange}
                placeholder="Account number"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>IFSC Code <span>*</span></Label>
              <Input
                type="text"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                placeholder="SBIN0001234"
                required
              />
            </FormGroup>
          </FormRow>
          <FullWidthRow>
            <FormGroup>
              <Label>Cancelled Cheque URL</Label>
              <Input
                type="text"
                name="cancelledChequeUrl"
                value={formData.cancelledChequeUrl}
                onChange={handleInputChange}
                placeholder="URL to cancelled cheque image"
              />
            </FormGroup>
          </FullWidthRow>
        </FormSection>

        {/* Location Details */}
        <FormSection>
          <SectionTitle icon="📍">Location Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>State <span>*</span></Label>
              <Input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Karnataka"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Region <span>*</span></Label>
              <Input
                type="text"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                placeholder="Dharwad"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Primary Crop Type */}
        <FormSection>
          <SectionTitle icon="🌱">Primary Crop Type</SectionTitle>
          <FullWidthRow>
            <FormGroup>
              <Label>Primary Crop Type <span>*</span></Label>
              <Input
                type="text"
                name="primaryCropType"
                value={formData.primaryCropType}
                onChange={handleInputChange}
                placeholder="e.g., Mango, Tomato, Onion"
                required
              />
            </FormGroup>
          </FullWidthRow>
        </FormSection>

        {/* Multiple Farms */}
        <FormSection>
          <SectionTitle icon="🚜">Multiple Farm Details</SectionTitle>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Add all your farms. You can add crops for each farm separately.</p>
          
          {farms.map((farm, farmIndex) => (
            <div key={farmIndex} style={{ marginBottom: '2.5rem', padding: '1.5rem', background: '#f5f5f5', borderRadius: '6px', border: '1px solid #ddd' }}>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#667eea', fontSize: '1rem' }}>Farm {farmIndex + 1}</h3>
                {farms.length > 1 && (
                  <Button 
                    className="secondary" 
                    type="button" 
                    onClick={() => removeFarmField(farmIndex)}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', background: '#f44336', color: 'white', border: 'none' }}
                  >
                    ✕ Remove Farm
                  </Button>
                )}
              </div>

              <FormRow>
                <FormGroup>
                  <Label>Farm Name <span>*</span></Label>
                  <Input
                    type="text"
                    value={farm.farmName}
                    onChange={(e) => handleFarmChange(farmIndex, 'farmName', e.target.value)}
                    placeholder="My Green Farm"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Area in Acres <span>*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={farm.areaInAcres}
                    onChange={(e) => handleFarmChange(farmIndex, 'areaInAcres', e.target.value)}
                    placeholder="10.5"
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    value={farm.latitude}
                    onChange={(e) => handleFarmChange(farmIndex, 'latitude', e.target.value)}
                    placeholder="15.4167"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    value={farm.longitude}
                    onChange={(e) => handleFarmChange(farmIndex, 'longitude', e.target.value)}
                    placeholder="75.9167"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Address Line 1 <span>*</span></Label>
                  <Input
                    type="text"
                    value={farm.addressLine1}
                    onChange={(e) => handleFarmChange(farmIndex, 'addressLine1', e.target.value)}
                    placeholder="Farm address"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Address Line 2</Label>
                  <Input
                    type="text"
                    value={farm.addressLine2}
                    onChange={(e) => handleFarmChange(farmIndex, 'addressLine2', e.target.value)}
                    placeholder="Additional details"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>District <span>*</span></Label>
                  <Input
                    type="text"
                    value={farm.district}
                    onChange={(e) => handleFarmChange(farmIndex, 'district', e.target.value)}
                    placeholder="Dharwad"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Pincode</Label>
                  <Input
                    type="text"
                    value={farm.pincode}
                    onChange={(e) => handleFarmChange(farmIndex, 'pincode', e.target.value)}
                    placeholder="580001"
                  />
                </FormGroup>
              </FormRow>

              {/* Crops for this farm */}
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'white', borderRadius: '4px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#059669', fontSize: '0.95rem' }}>Crops for Farm {farmIndex + 1}</h4>
                
                {farm.crops.map((crop, cropIndex) => (
                  <div key={cropIndex}>
                    <FormRow>
                      <FormGroup>
                        <Label>Crop Type <span>*</span></Label>
                        <Input
                          type="text"
                          value={crop.cropType}
                          onChange={(e) => handleFarmCropChange(farmIndex, cropIndex, 'cropType', e.target.value)}
                          placeholder="e.g., Mango"
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Distribution % <span>*</span></Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={crop.distributionPercentage}
                          onChange={(e) => handleFarmCropChange(farmIndex, cropIndex, 'distributionPercentage', e.target.value)}
                          placeholder="50"
                          required
                        />
                      </FormGroup>
                    </FormRow>
                    {farm.crops.length > 1 && (
                      <Button 
                        className="secondary" 
                        type="button" 
                        onClick={() => removeFarmCropField(farmIndex, cropIndex)}
                        style={{ marginBottom: '1rem', padding: '0.4rem 0.8rem', fontSize: '0.7rem' }}
                      >
                        ✕ Remove Crop
                      </Button>
                    )}
                  </div>
                ))}

                <Button 
                  className="secondary" 
                  type="button" 
                  onClick={() => addFarmCropField(farmIndex)}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 0.8rem', fontSize: '0.7rem' }}
                >
                  + Add Crop to Farm {farmIndex + 1}
                </Button>
              </div>
            </div>
          ))}

          <Button 
            className="secondary" 
            type="button" 
            onClick={addFarmField}
            style={{ marginTop: '1.5rem', padding: '0.5rem 1rem', fontSize: '0.85rem' }}
          >
            + Add Another Farm
          </Button>
        </FormSection>

        <ButtonGroup>
          <Button className="primary" type="submit" disabled={loading}>
            {loading ? '⏳ Registering...' : '✓ Register as Seller'}
          </Button>
        </ButtonGroup>

        <FormFooter>
          © 2026 Neu Edge Software Private Limited. All rights reserved.
        </FormFooter>
      </form>
    </FormWrapper>
    </Container>
  );
};

// Farm and Crop Details sections fully implemented - Ready for production
export default SellerRegistrationForm;
